import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { amazonColors, amazonShadows } from "../../../config/amazonTheme";
import { ROUTES } from "../../../config/constants";
import { useExchangeRate } from "../../../hooks/useExchangeRate";
import { convertUsdToTjs } from "../../../services/exchangeRateService";
import { useCartStore } from "../../../store/useCartStore";
import Price from "../../common/Price";

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ open, onClose }) => {
  const { t: myTranslate } = useTranslation();
  const navigate = useNavigate();
  const {
    items,
    removeFromCart,
    updateQuantity,
    getItemCount,
    getTotalPrice,
    getTotalShippingCost,
    getTotalWithShipping,
  } = useCartStore();
  const { data: exchangeRate } = useExchangeRate();

  const handleRemoveItem = (productId: string) => {
    removeFromCart(productId);
  };

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= 99) {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleViewCart = () => {
    onClose();
    navigate(ROUTES.CART);
  };

  const handleCheckout = () => {
    onClose();
    navigate(ROUTES.CHECKOUT);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 400 },
          backgroundColor: "#fff",
        },
      }}
    >
      <Box
        sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            className="notranslate"
            translate="no"
            sx={{ color: amazonColors.text.primary }}
          >
            {myTranslate("cart.cartItems", { count: getItemCount() })}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Cart Items */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {items.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <ShoppingCartIcon
                sx={{ fontSize: 48, color: amazonColors.gray[400], mb: 2 }}
              />
              <Typography
                variant="h6"
                className="notranslate"
                translate="no"
                sx={{ mb: 1, color: amazonColors.text.primary }}
              >
                {myTranslate("cart.yourCartIsEmpty")}
              </Typography>
              <Typography
                variant="body2"
                className="notranslate"
                translate="no"
                sx={{ color: amazonColors.text.secondary }}
              >
                {myTranslate("cart.addSomeProducts")}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {items.map((item) => (
                <Card
                  key={item.cartItemId}
                  sx={{ boxShadow: amazonShadows.sm }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2}>
                      {/* Product Image */}
                      <Box sx={{ width: 60, height: 60, flexShrink: 0 }}>
                        <Box
                          component="img"
                          src={
                            item.product.image ||
                            item.product.main_image ||
                            "/placeholder-product.png"
                          }
                          alt={item.product.title}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                      </Box>

                      {/* Product Details */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: amazonColors.text.primary,
                            mb: 0.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            fontWeight: 500,
                          }}
                        >
                          {item.product.title}
                        </Typography>

                        <Typography
                          variant="caption"
                          className="notranslate"
                          translate="no"
                          sx={{ color: amazonColors.text.secondary, mb: 0.5 }}
                        >
                          {myTranslate("cart.qty", { quantity: item.quantity })}
                        </Typography>

                        <Typography
                          variant="body2"
                          translate="no"
                          sx={{
                            color: amazonColors.orange,
                            fontWeight: "bold",
                          }}
                        >
                          <Price value={Number(item.product.price) + (item.offerShippingFee || 0)} tjsAmount={convertUsdToTjs(Number(item.product.price) + (item.offerShippingFee || 0), exchangeRate ?? null)} />
                        </Typography>
                      </Box>

                      {/* Remove Button */}
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleRemoveItem(item.cartItemId)
                        }
                        sx={{
                          color: amazonColors.red,
                          alignSelf: "flex-start",
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>

        {/* Footer */}
        {items.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />

            {/* Items Total */}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}
            >
              <Typography
                variant="body2"
                className="notranslate"
                translate="no"
                sx={{ color: amazonColors.text.secondary }}
              >
                {myTranslate("cart.items", { count: getItemCount() })}
              </Typography>
              <Typography
                variant="body2"
                translate="no"
                sx={{ color: amazonColors.text.secondary }}
              >
                <Price value={getTotalPrice()} disableCentsHeuristic tjsAmount={convertUsdToTjs(getTotalPrice(), exchangeRate ?? null)} />
              </Typography>
            </Box>

            {/* Total */}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography
                variant="h6"
                className="notranslate"
                translate="no"
                sx={{ fontWeight: 600 }}
              >
                {myTranslate("cart.total")}
              </Typography>
              <Typography
                variant="h6"
                translate="no"
                sx={{ color: amazonColors.orange, fontWeight: 700 }}
              >
                <Price value={getTotalPrice()} disableCentsHeuristic tjsAmount={convertUsdToTjs(getTotalPrice(), exchangeRate ?? null)} />
              </Typography>
            </Box>

            <Stack spacing={1}>
              <Button
                variant="contained"
                fullWidth
                className="notranslate"
                translate="no"
                onClick={handleCheckout}
                sx={{
                  backgroundColor: amazonColors.orange,
                  color: "white",
                  fontWeight: "bold",
                  "&:hover": { backgroundColor: amazonColors.orangeHover },
                }}
              >
                {myTranslate("cart.checkout")}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                className="notranslate"
                translate="no"
                onClick={handleViewCart}
                sx={{
                  borderColor: amazonColors.blue,
                  color: amazonColors.blue,
                  "&:hover": {
                    borderColor: amazonColors.blueHover,
                    backgroundColor: "rgba(0, 113, 133, 0.04)",
                  },
                }}
              >
                {myTranslate("cart.viewCart")}
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default CartDrawer;
